/**
 * Choose where an excerpt goes (#277): type-ahead over every destination type
 * - meetings, tasks, notes, scratchpads, projects, series, tags, Views and
 * people - multi-select, with recent destinations offered before typing.
 *
 * #360: every series also offers its "next meeting" - the upcoming meeting it
 * already has, or one the server creates on the series' next date. A series
 * with no schedule asks for the date (default tomorrow).
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Scissors, X, Check, NotebookPen, CalendarClock } from 'lucide-react';
import { apiGet } from '../../lib/api';
import { ENTITY_ICONS } from '../../lib/entityMeta';
import { getNextMeeting, shortDate, NEXT_MEETING, type DestType, type ItemRef, type NextMeetingPreview } from '../../lib/excerpts';
import { localToday } from '../../lib/lists';

export type PickType = DestType | typeof NEXT_MEETING;

export interface PickedItem {
  type: PickType;
  id: string;
  title: string;
  /** Next meeting only: the date to create it on, for a series with no schedule. */
  date?: string;
  /** Next meeting only: the meeting the preview expected it to reach. */
  meetingId?: string;
}

const DEST_TYPES = new Set<string>(['meeting_instance', 'task', 'note', 'scratchpad', 'scope', 'meeting_series', 'tag', 'reference_doc', 'person', NEXT_MEETING]);
const TYPE_LABEL: Record<string, string> = {
  meeting_instance: 'Meeting', task: 'Task', note: 'Note', scratchpad: 'Scratchpad', scope: 'Project',
  meeting_series: 'Series', tag: 'Tag', reference_doc: 'View', person: 'Person', [NEXT_MEETING]: 'Next meeting',
};
const RECENT_KEY = 'dossier:excerptRecentDestinations';
const MAX_RECENT = 6;

export function getRecentDestinations(): PickedItem[] {
  try {
    const raw = JSON.parse(localStorage.getItem(RECENT_KEY) ?? '[]');
    return Array.isArray(raw) ? raw.filter((r) => r && DEST_TYPES.has(r.type) && typeof r.id === 'string' && typeof r.title === 'string') : [];
  } catch {
    return [];
  }
}

export function rememberDestinations(items: PickedItem[]): void {
  try {
    const keyed = (i: PickedItem) => `${i.type}:${i.id}`;
    // A next meeting is remembered as the series' next meeting, not as a date or a meeting.
    const plain = items.map(({ type, id, title }) => ({ type, id, title }));
    const fresh = new Set(plain.map(keyed));
    const next = [...plain, ...getRecentDestinations().filter((r) => !fresh.has(keyed(r)))].slice(0, MAX_RECENT);
    localStorage.setItem(RECENT_KEY, JSON.stringify(next));
  } catch {
    // Storage unavailable (private window): recents are a convenience only.
  }
}

interface SearchResult { entity_type: string; entity_id: string; name: string }

interface Props {
  title?: string;
  confirmLabel?: string;
  initial?: PickedItem[];
  /** The excerpt's own document: never offered. */
  exclude?: ItemRef;
  onConfirm: (picked: PickedItem[]) => void;
  onClose: () => void;
}

const keyOf = (i: { type: string; id: string }) => `${i.type}:${i.id}`;
const NEXT_SUFFIX = ' - next meeting';
const seriesName = (title: string) => (title.endsWith(NEXT_SUFFIX) ? title.slice(0, -NEXT_SUFFIX.length) : title);

/** A next meeting must be sent with a chosen date only when its series has no schedule; that date must be today or later. */
const dateOk = (p: PickedItem, today: string) => p.type !== NEXT_MEETING || p.date === undefined || (/^\d{4}-\d{2}-\d{2}$/.test(p.date) && p.date >= today);

export function DestinationPicker({ title = 'Send excerpt to', confirmLabel = 'Send', initial = [], exclude, onConfirm, onClose }: Props) {
  const [picked, setPicked] = useState<PickedItem[]>(initial);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<PickedItem[]>([]);
  const [active, setActive] = useState(0);
  // Series id -> what its next meeting is now; null when the preview failed.
  const [previews, setPreviews] = useState<Record<string, NextMeetingPreview | null>>({});
  const requested = useRef(new Set<string>());
  const inputRef = useRef<HTMLInputElement>(null);
  const today = localToday();

  useEffect(() => { inputRef.current?.focus(); }, []);

  useEffect(() => {
    const q = query.trim();
    if (!q) { setResults([]); return; }
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const data = await apiGet<SearchResult[]>(`/entity-search?q=${encodeURIComponent(q)}&limit=20`);
        if (cancelled) return;
        setResults(data
          .filter((r) => DEST_TYPES.has(r.entity_type))
          .map((r) => ({ type: r.entity_type as DestType, id: r.entity_id, title: r.name })));
        setActive(0);
      } catch {
        if (!cancelled) setResults([]);
      }
    }, 150);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [query]);

  const excludedKey = exclude ? keyOf(exclude) : '';
  const base = useMemo(() => {
    const list = query.trim() ? results : getRecentDestinations();
    const out: PickedItem[] = [];
    const seen = new Set<string>();
    const add = (o: PickedItem) => { if (!seen.has(keyOf(o)) && keyOf(o) !== excludedKey) { seen.add(keyOf(o)); out.push(o); } };
    for (const o of list) {
      add(o);
      // Each series is followed by its next meeting.
      if (o.type === 'meeting_series') add({ type: NEXT_MEETING, id: o.id, title: `${o.title}${NEXT_SUFFIX}` });
    }
    return out;
  }, [query, results, excludedKey]);

  // Fetch what each offered next meeting would reach, once per series.
  const nextIds = base.filter((o) => o.type === NEXT_MEETING).map((o) => o.id).join(',');
  useEffect(() => {
    for (const id of nextIds ? nextIds.split(',') : []) {
      if (requested.current.has(id)) continue;
      requested.current.add(id);
      getNextMeeting(id)
        .then((p) => setPreviews((cur) => ({ ...cur, [id]: p })))
        .catch(() => setPreviews((cur) => ({ ...cur, [id]: null })));
    }
  }, [nextIds]);

  // An archived series has no next meeting to offer, and an excerpt is never sent to its own document.
  const options = base.filter((o) => {
    if (o.type !== NEXT_MEETING) return true;
    const p = previews[o.id];
    return !p || (!p.archived && !(exclude?.type === 'meeting_instance' && p.meeting?.id === exclude.id));
  });

  const labelOf = (o: PickedItem): string => {
    if (o.type !== NEXT_MEETING) return o.title;
    const p = previews[o.id];
    if (!p) return o.title;
    const name = `${p.series_name}${NEXT_SUFFIX}`;
    if (p.meeting) return `${name}, ${shortDate(p.meeting.meeting_date)}`;
    if (p.next_date) return `${name} (new), ${shortDate(p.next_date)}`;
    return `${name} (choose a date)`;
  };

  /** A next meeting as picked: what it should reach, and a date (default tomorrow) when one must be chosen. */
  const asPicked = (o: PickedItem): PickedItem => {
    if (o.type !== NEXT_MEETING) return o;
    const p = previews[o.id];
    const name = `${p ? p.series_name : seriesName(o.title)}${NEXT_SUFFIX}`;
    if (p?.meeting) return { type: NEXT_MEETING, id: o.id, title: name, meetingId: p.meeting.id };
    if (p && !p.next_date && !p.archived) {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      return { type: NEXT_MEETING, id: o.id, title: name, date: localToday(tomorrow) };
    }
    return { type: NEXT_MEETING, id: o.id, title: name };
  };

  // A next meeting picked before its preview arrived gets its meeting, or its date prompt, once it does.
  useEffect(() => {
    setPicked((cur) => {
      let changed = false;
      const next = cur.map((p) => {
        if (p.type !== NEXT_MEETING || p.date !== undefined || p.meetingId || !previews[p.id]) return p;
        const filled = asPicked(p);
        if (filled.date === undefined && !filled.meetingId) return p;
        changed = true;
        return filled;
      });
      return changed ? next : cur;
    });
    // asPicked reads only `previews`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previews]);

  const pickedKeys = new Set(picked.map(keyOf));
  const toggle = (item: PickedItem) => {
    setPicked((cur) => (cur.some((c) => keyOf(c) === keyOf(item)) ? cur.filter((c) => keyOf(c) !== keyOf(item)) : [...cur, asPicked(item)]));
  };
  const setDate = (item: PickedItem, date: string) => {
    setPicked((cur) => cur.map((c) => (keyOf(c) === keyOf(item) ? { ...c, date } : c)));
  };
  const datesOk = picked.every((p) => dateOk(p, today));
  const canSend = (picked.length > 0 || initial.length > 0) && datesOk;
  const confirm = () => {
    if (!canSend) return;
    rememberDestinations(picked);
    onConfirm(picked);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') { e.preventDefault(); onClose(); return; }
    // The date field needs its own arrow and Enter keys.
    if ((e.target as HTMLElement).getAttribute?.('type') === 'date' && e.key !== 'Enter') return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => Math.min(a + 1, options.length - 1)); return; }
    if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); return; }
    if (e.key === 'Enter') {
      e.preventDefault();
      if ((e.ctrlKey || e.metaKey) || options.length === 0) { confirm(); return; }
      const o = options[active];
      if (o) { toggle(o); setQuery(''); }
    }
  };

  const iconOf = (type: string) => (type === NEXT_MEETING ? CalendarClock : ENTITY_ICONS[type] ?? NotebookPen);

  return createPortal(
    <div className="fixed inset-0 z-[70] flex items-start justify-center bg-black/50 pt-[12vh] px-4" onMouseDown={onClose}>
      <div
        role="dialog"
        aria-label={title}
        className="w-full max-w-md rounded-lg border border-border bg-bg-surface shadow-xl"
        onMouseDown={(e) => e.stopPropagation()}
        onKeyDown={onKeyDown}
      >
        <div className="flex items-center gap-2 px-3 py-2 border-b border-border">
          <Scissors size={14} className="text-excerpt" />
          <span className="text-sm font-medium text-text-primary flex-1">{title}</span>
          <button type="button" aria-label="Close" onClick={onClose} className="text-text-muted hover:text-text-primary"><X size={14} /></button>
        </div>
        {picked.length > 0 && (
          <div className="flex flex-wrap gap-1 px-3 pt-2">
            {picked.map((p) => {
              const Icon = iconOf(p.type);
              return (
                <span key={keyOf(p)} className="inline-flex items-center gap-1 rounded bg-bg-input px-1.5 py-0.5 text-xs text-text-secondary">
                  <Icon size={11} />
                  <span className="max-w-[12rem] truncate">{p.title}</span>
                  {p.type === NEXT_MEETING && p.date !== undefined && (
                    <input
                      type="date"
                      aria-label={`Date for ${p.title}`}
                      min={today}
                      value={p.date}
                      onChange={(e) => setDate(p, e.target.value)}
                      className={`rounded bg-bg-surface px-1 text-xs text-text-primary outline-none focus:ring-1 focus:ring-accent ${dateOk(p, today) ? '' : 'ring-1 ring-danger'}`}
                    />
                  )}
                  <button type="button" aria-label={`Remove ${p.title}`} onClick={() => toggle(p)} className="hover:text-danger"><X size={11} /></button>
                </span>
              );
            })}
          </div>
        )}
        <div className="px-3 py-2">
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search tasks, projects, people, tags..."
            aria-label="Search destinations"
            className="w-full rounded bg-bg-input px-2 py-1.5 text-sm text-text-primary placeholder:text-text-muted outline-none focus:ring-1 focus:ring-accent"
          />
        </div>
        <ul role="listbox" aria-label="Destinations" className="max-h-64 overflow-y-auto pb-1">
          {!query.trim() && options.length > 0 && <li className="px-3 py-1 text-[10px] uppercase tracking-wide text-text-muted">Recent</li>}
          {options.map((o, i) => {
            const Icon = iconOf(o.type);
            const on = pickedKeys.has(keyOf(o));
            return (
              <li
                key={keyOf(o)}
                role="option"
                aria-selected={on}
                onMouseEnter={() => setActive(i)}
                onMouseDown={(e) => { e.preventDefault(); toggle(o); }}
                className={`flex items-center gap-2 px-3 py-1.5 text-sm cursor-pointer ${i === active ? 'bg-bg-hover' : ''} ${o.type === NEXT_MEETING ? 'pl-7' : ''}`}
              >
                <Icon size={13} className="shrink-0 text-text-muted" />
                <span className="flex-1 truncate text-text-primary">{labelOf(o)}</span>
                <span className="text-[10px] text-text-muted">{TYPE_LABEL[o.type]}</span>
                {on && <Check size={13} className="text-accent" />}
              </li>
            );
          })}
          {query.trim() && options.length === 0 && <li className="px-3 py-2 text-xs text-text-muted">No matches</li>}
        </ul>
        <div className="flex items-center justify-between gap-2 px-3 py-2 border-t border-border">
          <span className="text-[10px] text-text-muted">{datesOk ? 'Enter picks, Ctrl+Enter sends' : 'Choose a date from today on'}</span>
          <button
            type="button"
            onClick={confirm}
            disabled={!canSend}
            className="rounded bg-accent px-3 py-1 text-xs font-medium text-white disabled:opacity-40"
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
